import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  createSalesShipment,
  getSalesOrderInTransitOpen,
} from "@/shared/api/sales";
import { notify } from "@/shared/feedback/notify";

export function SalesShipmentNewPage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const salesOrderId = searchParams.get("salesOrderId") ?? "";
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitOnCreate, setSubmitOnCreate] = useState(true);
  const [orderNumber, setOrderNumber] = useState("");
  const [lines, setLines] = useState<
    Array<{
      salesOrderLineId: string;
      productName: string;
      qtyRemaining: string;
      qty: string;
    }>
  >([]);

  useEffect(() => {
    if (!companySlug || !salesOrderId) return;
    let cancelled = false;
    getSalesOrderInTransitOpen(companySlug, salesOrderId)
      .then((res) => {
        if (cancelled) return;
        setOrderNumber(res.order.number);
        setLines(
          res.lines.map((l) => ({
            salesOrderLineId: l.salesOrderLineId,
            productName: l.productName,
            qtyRemaining: l.qtyRemaining,
            qty: l.qtyRemaining,
          })),
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed";
        if (msg === "Not signed in") navigate("/login", { replace: true });
        else setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, salesOrderId, navigate]);

  async function onCreate() {
    if (!companySlug || !salesOrderId) return;
    setBusy(true);
    setError(null);
    try {
      const payloadLines = lines
        .filter((l) => Number(l.qty) > 0)
        .map((l) => ({
          salesOrderLineId: l.salesOrderLineId,
          qty: l.qty,
        }));
      if (payloadLines.length === 0) {
        setError("Enter qty on at least one line");
        return;
      }
      const { shipment } = await createSalesShipment(companySlug, {
        salesOrderId,
        lines: payloadLines,
        submit: submitOnCreate,
      });
      notify.added(shipment.number);
      navigate(`/c/${companySlug}/sales/in-transit/${shipment.id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setBusy(false);
    }
  }

  if (!companySlug) return null;

  return (
    <PageFrame width="narrow">
      <DeskDocBackLink
        listBasePath={`/c/${companySlug}/sales/in-transit`}
        listLabel="In transit"
      />
      <PageHeader
        eyebrow="In transit"
        title="New shipment"
        description={
          salesOrderId
            ? `From ${orderNumber || "sales order"} — logistics only; stock moves on delivery.`
            : "Open Create ▾ → In transit from a sales order."
        }
      />
      {!salesOrderId ? (
        <Alert variant="destructive" className="mt-4">
          Missing salesOrderId.{" "}
          <Link to={`/c/${companySlug}/sales/orders`} className="underline">
            Pick an order
          </Link>
        </Alert>
      ) : null}
      {error ? (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      ) : null}
      {salesOrderId && lines.length === 0 && !error ? (
        <Alert variant="muted" className="mt-4">
          Nothing left to ship (fully delivered or already in transit).
        </Alert>
      ) : null}
      {lines.length > 0 ? (
        <div className="mt-6 space-y-3">
          {lines.map((line) => (
            <div
              key={line.salesOrderLineId}
              className="flex flex-wrap items-end gap-3 border-b border-[var(--color-border)] pb-3"
            >
              <div className="min-w-[10rem] flex-1">
                <p className="m-0 text-sm font-medium">{line.productName}</p>
                <p className="m-0 text-xs text-[var(--color-muted)]">
                  Remaining {line.qtyRemaining}
                </p>
              </div>
              <div>
                <Label htmlFor={`qty-${line.salesOrderLineId}`}>Qty</Label>
                <Input
                  id={`qty-${line.salesOrderLineId}`}
                  className="mt-1 w-28 font-mono"
                  inputMode="decimal"
                  value={line.qty}
                  onChange={(e) =>
                    setLines((prev) =>
                      prev.map((l) =>
                        l.salesOrderLineId === line.salesOrderLineId
                          ? { ...l, qty: e.target.value }
                          : l,
                      ),
                    )
                  }
                />
              </div>
            </div>
          ))}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={submitOnCreate}
              onChange={(e) => setSubmitOnCreate(e.target.checked)}
            />
            Submit on create (open in transit)
          </label>
          <Button
            type="button"
            disabled={busy || !salesOrderId}
            pending={busy}
            onClick={() => void onCreate()}
          >
            {submitOnCreate ? "Create & submit" : "Save draft"}
          </Button>
        </div>
      ) : null}
    </PageFrame>
  );
}
